/**
 * The notification items of the menus (2026-10-29), as Discord has them:
 * "Mute server / category / channel ›" (15 minutes, 1, 3, 8 or 24 hours,
 * until turned back on) or "Unmute …" with when it ends, and "Notification
 * settings ›" with the levels (or, on the server's menu, the dialog). Built
 * when the menu opens, from the store as it is then; nothing while the
 * Console's `notifications` flag is off. On a phone the menus are sheets.
 */
import {
  ChannelType,
  NotificationLevel,
  muteActive,
  overrideOf,
  type Channel,
  type MuteDuration,
  type Snowflake,
} from '@neosocial/protocol';
import { tr } from '../i18n';
import { useData } from '../store/data';
import type { MenuItem } from '../ui/overlay';
import { muteChannel, muteGuild, setChannelLevel, unmuteChannel, unmuteGuild } from './actions';
import { DURATIONS, durationLabel, levelName, mutedUntil } from './labels';
import { openNotifyDialog } from './NotifyDialog';
import { inheritedOf, isGuildMuted, isMuted, levelOf, notifyOn, settingsIn } from './rules';

/** The durations, as Discord lists them. */
export function durationItems(run: (w: MuteDuration) => void): MenuItem[] {
  return DURATIONS.map((w) => ({ id: `nt-${w}`, label: durationLabel(w), run: () => run(w) }));
}

/** The server's items: Mute / Unmute, and the dialog. */
export function guildNotifyItems(guildId: Snowflake | null): MenuItem[] {
  if (!guildId || !notifyOn()) return [];
  const { t } = tr();
  const d = useData.getState();
  const s = settingsIn(d, guildId);
  const items: MenuItem[] = [];
  if (isGuildMuted(d, guildId))
    items.push({
      id: 'nt-unmute',
      label: t('nt.unmuteServer'),
      sub: mutedUntil(s?.mute_config),
      icon: 'bell',
      run: () => void unmuteGuild(guildId),
    });
  else
    items.push({
      id: 'nt-mute',
      label: t('nt.muteServer'),
      icon: 'bell-off',
      submenu: () => durationItems((w) => void muteGuild(guildId, w)),
    });
  items.push({
    id: 'nt-settings',
    label: t('nt.settings'),
    icon: 'bell',
    run: () => openNotifyDialog(guildId),
  });
  return items;
}

/** A category's or channel's items: Mute / Unmute, and its levels. */
export function channelNotifyItems(c: Channel): MenuItem[] {
  const guildId = c.guild_id;
  if (!guildId || !notifyOn()) return [];
  const { t } = tr();
  const d = useData.getState();
  const cat = c.type === ChannelType.GUILD_CATEGORY;
  const own = overrideOf(settingsIn(d, guildId), c.id);
  const items: MenuItem[] = [];
  if (own && muteActive(own.muted, own.mute_config, Date.now()))
    items.push({
      id: 'nt-unmute',
      label: t(cat ? 'nt.unmuteCategory' : 'nt.unmuteChannel'),
      sub: mutedUntil(own.mute_config),
      icon: 'bell',
      run: () => void unmuteChannel(guildId, c.id),
    });
  else
    items.push({
      id: 'nt-mute',
      label: t(cat ? 'nt.muteCategory' : 'nt.muteChannel'),
      // muted all the same through its category or the server: says so
      sub: isMuted(d, c)
        ? isGuildMuted(d, guildId)
          ? t('nt.withServer')
          : t('nt.withCategory')
        : undefined,
      icon: 'bell-off',
      submenu: () => durationItems((w) => void muteChannel(guildId, c.id, w)),
    });
  items.push({
    id: 'nt-level',
    label: t('nt.settings'),
    sub: levelName(levelOf(d, c)),
    icon: 'bell',
    submenu: () => levelItems(c),
  });
  return items;
}

/** "Use … default", All messages, Only @mentions, Nothing: one of them checked. */
function levelItems(c: Channel): MenuItem[] {
  const { t } = tr();
  const d = useData.getState();
  const guildId = c.guild_id!;
  const cur = overrideOf(settingsIn(d, guildId), c.id)?.message_notifications ?? 3;
  const set = (l: NotificationLevel) => () => void setChannelLevel(guildId, c.id, l);
  const inheritsCategory = c.type !== ChannelType.GUILD_CATEGORY && !!c.parent_id;
  return [
    {
      id: 'nt-l3',
      label: t(inheritsCategory ? 'nt.useCategory' : 'nt.useServer'),
      sub: levelName(inheritedOf(d, c)),
      checked: cur === NotificationLevel.INHERIT,
      run: set(NotificationLevel.INHERIT),
    },
    { sep: true },
    {
      id: 'nt-l0',
      label: t('nt.all'),
      checked: cur === 0,
      run: set(NotificationLevel.ALL_MESSAGES),
    },
    {
      id: 'nt-l1',
      label: t('nt.mentions'),
      checked: cur === 1,
      run: set(NotificationLevel.ONLY_MENTIONS),
    },
    {
      id: 'nt-l2',
      label: t('nt.nothing'),
      checked: cur === 2,
      run: set(NotificationLevel.NO_MESSAGES),
    },
  ];
}
